<template>
  <div class="performance-wrapper">
    <h1 class="title-screen" style="font-size: 20px">{{ $adminT('performances.dates', 'Performance dates') }}</h1>
    <div v-for="(performance, index) in vs.$each.$iter" :key="performance.id">
      <performance
        :initial-values="performance"
        :index="parseInt(index)"
        :organization-slug="organizationSlug"
        :venues="venues"
        :promo-codes="promoCodes"
        :ticket-types="ticketTypes"
        :vs="performance"
        :ticket-sellers="ticketSellers"
        :active-subscriptions="activeSubscriptions"
        @duplicatePerformance="handleDuplicatePerformance"
        @refetchOrderItemAndVenue="handleRefetchOrderItemAndVenue"
        @removePerformance="removePerformance"
        @removeSelectedTicketSellers="removeSelectedTicketSellers"
      />
    </div>
    <main-button style="margin-top: 8px" @click="addPerformance">
      <span class="add-symbol">+</span>{{ $adminT('performances.addDate', 'Add date') }}
    </main-button>
    <theater-add-tax-panel
      v-if="Object.keys(vs.$each.$iter).length"
      :value="vatPercentage"
      @input="$emit('update:vatPercentage', $event)"
    />
    <div class="buttons-wrapper" style="margin-top: 58px">
      <main-button :text="$adminT('performances.cancel', 'CANCEL')" flat depressed @click="goBack()" />
      <div class="buttons">
        <main-button flat outline @click="$emit('back')">
          <v-icon class="arrow-back"> arrow_back </v-icon>
          <span>{{ $adminT('performances.back', 'back') }}</span>
        </main-button>
        <main-button
          :text="$adminT('performances.createEvent', 'CREATE EVENT')"
          :disabled="submittingForm"
          @click="handleEmitCreatePerformance()"
        />
      </div>
    </div>
    <simple-snackbar :open.sync="snackbar.open" :color="snackbar.color" :message-prop="workflowLabel(localizedSnackbarMessage)" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import performance from './performance.vue'
import { v4 as uuid } from 'uuid'
import { GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_PROMO_CODES } from '@/graphql/orderItemTemplate.graphql'
import { GET_NON_BOX_OFFICE_TICKET_SELLERS } from '@/graphql/user.graphql'
import { getTheaterSubscriptions } from '@/graphql/theater-subscriptions.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import TheaterAddTaxPanel from './TheaterAddTaxPanel.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'performance-container',
  components: {
    SimpleSnackbar,
    performance,
    TheaterAddTaxPanel,
  },
  props: {
    initialValues: {
      type: Array,
      default: () => [],
    },
    vs: {
      type: Object,
      default: () => {},
    },
    organizationSlug: String,
    submittingForm: {
      type: Boolean,
      default: false,
    },
    vatPercentage: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      form: this.initialValues,
      venues: [],
      ticketTypes: [],
      promoCodes: [],
      ticketSellers: [],
      activeSubscriptions: [],
      snackbar: {
        open: false,
        messageProp: '',
        color: 'primary',
      },
    }
  },
  computed: {
    localizedSnackbarMessage() {
      switch (this.snackbar.messageProp) {
      case 'performances.error':
        return this.$adminT('performances.error', 'Oops, Something went wrong!')
      case 'performances.required':
        return this.$adminT('performances.required', 'Fill in the required information')
      default:
        return ''
      }
    },
  },
  watch: {
    venues: function (val) {
      this.$emit('venueChange', val)
    },
  },
  mounted() {
    this.fetchTicketTypesAndVenues()
    this.fetchTicketSellers()
    this.fetchActiveSubscriptions()
  },
  methods: {
    handleDuplicatePerformance(performanceData) {
      const { id, date, time, ...info } = performanceData
      this.form.push({
        ...info,
        id: uuid(),
        addExtraSeats: !!performanceData.additionalCapacity,
        isIncludedInSubscription: info.isIncludedInSubscription
          ? info.isIncludedInSubscription
          : false,
      })
    },
    async handleRefetchOrderItemAndVenue() {
      await this.fetchTicketTypesAndVenues()
      await this.fetchActiveSubscriptions()
    },
    addPerformance() {
      this.form.push({
        id: uuid(),
        orderItemTemplateIds: [],
        addExtraSeats: false,
        metadata: { theaterOnlineSales: true },
        isIncludedInSubscription: false,
      })
    },
    removePerformance(id) {
      this.form = this.form.filter(performance => performance.id !== id)
      this.$emit('removePerformance', this.form)
    },
    handleEmitCreatePerformance() {
      this.vs.$touch()

      if (this.vs.$invalid) {
        this.snackbar = {
          open: true,
          messageProp: 'performances.required',
          color: 'error',
        }

        return
      }

      this.removePromoCodesFromPerformancesWithoutRegularTicket()
      this.$emit('createPerformance', this.form)
    },
    removePromoCodesFromPerformancesWithoutRegularTicket() {
      const allOrganizationRegularTicketIds = this.ticketTypes
        .filter(t => !t.membership && !t.ticketSeller && t.prepaidCoupon)
        .map(t => t.id)
      this.form = this.form.map(p => {
        const selectedTicketTypesIncludeRegularTicket = p.orderItemTemplateIds.some(id =>
          allOrganizationRegularTicketIds.includes(id),
        )
        if (!selectedTicketTypesIncludeRegularTicket) {
          p.promoCodeIds = []
        }

        return p
      })
    },
    async fetchTicketTypesAndVenues() {
      const {
        data: {
          getOrganizationOrderItemTemplates,
          getOrganizationVenues,
          getAllOrganizationPromoCodes,
        },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_PROMO_CODES,
        variables: {
          organizationSlug: this.organizationSlug,
          pagination: this.pagination,
          includeArchived: true,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'performances.error',
          color: 'error',
        }
      } else {
        this.venues = getOrganizationVenues.organizationVenues.map(venue => venue.venue)
        this.ticketTypes = getOrganizationOrderItemTemplates
        this.promoCodes = getAllOrganizationPromoCodes
      }
    },
    async fetchTicketSellers() {
      const {
        data: { getNonBoxOfficeTicketSellers: ticketSellers },
      } = await this.$apollo.query({
        query: GET_NON_BOX_OFFICE_TICKET_SELLERS,
        variables: { organizationSlug: this.organizationSlug },
      })

      this.ticketSellers = ticketSellers.map(ts => {
        return {
          ...ts,
          id: Number(ts.id),
        }
      })
    },
    async fetchActiveSubscriptions() {
      try {
        const { data } = await this.$apollo.query({
          query: getTheaterSubscriptions,
          variables: { organizationSlug: this.organizationSlug },
          fetchPolicy: 'no-cache',
        })
        const list = data && data.getTheaterSubscriptions ? data.getTheaterSubscriptions : []
        const active = list.filter(s => {
          return !!s.active
        })
        this.activeSubscriptions = active
      } catch (e) {
        // Non-blocking: silently ignore and leave list empty
        this.activeSubscriptions = []
      }
    },
    removeSelectedTicketSellers(performanceId) {
      const performance = this.form.find(item => item.id === performanceId)
      if (!performance) {
        return
      }

      performance.ticketSellerIds = undefined
    },
  },
}
</script>

<style scoped></style>
